/**
 * The register's two LinkedIn columns, in the words the product is allowed to use.
 *
 * WHAT THE PRODUCT CAN AND CANNOT MEASURE
 * ---------------------------------------
 * Certifier's help page counts «added to profile» and «shared» as separate engagement types, but it
 * does not say whether the first is measured by a click or by something LinkedIn confirms. Neither
 * was assumed. What this product observes is what it does itself:
 *
 *   - the recipient pressed «LinkedIn Profiline Ekle» here and was sent to LinkedIn;
 *   - a share was started for the record — the recipient was sent to LinkedIn's own share page,
 *     or this server began publishing on their behalf;
 *   - LinkedIn returned a post id to this server.
 *
 * Only the third is proof of anything happening on LinkedIn, so only the third may say
 * «Paylaşıldı». The first two say «başlatıldı», and the explanations below say so out loud rather
 * than leaving a badge to imply a completion nobody saw. LinkedIn sends no callback for a profile
 * entry, so «Eklendi» does not exist in this vocabulary at all.
 *
 * «Bilinmiyor» is NOT «yapılmadı». A record issued before any of this was recorded has a history
 * that genuinely was not kept, and printing «Eklenmedi» for it would be a claim about the past that
 * nobody made. That distinction is decided on the server (`domain/engagement.ts`), against the
 * instant from which absence started meaning something; the screen only prints the answer.
 */
export type ProfileAddStatus = 'not_applicable' | 'unknown' | 'none' | 'started';
export type ShareStatus = 'unknown' | 'none' | 'started' | 'result_unknown' | 'published';

export interface EngagementView {
  profileAdd: ProfileAddStatus;
  share: ShareStatus;
  publishedAt: string | null;
  postUrl: string | null;
}

interface Badge {
  label: string;
  /** The `title` a reader gets on hover, and what the row's explanation says. */
  hint: string;
  /** The pill class, reusing the reference's own vocabulary; no new colour is introduced. */
  tone: string;
}

export const PROFILE_ADD_BADGE: Record<ProfileAddStatus, Badge> = {
  started: {
    label: 'Ekleme başlatıldı',
    hint: "LinkedIn profil ekleme bağlantısı açıldı. LinkedIn'de kaydedildiği doğrulanmadı.",
    tone: 'pill-info',
  },
  none: {
    label: 'İşlem kaydı yok',
    hint: 'Takip başladığından beri bu kayıt için profil ekleme başlatılmadı.',
    tone: 'pill-neutral',
  },
  unknown: {
    label: 'Bilinmiyor',
    hint: 'Bu kayıt, etkileşim takibi başlamadan önce üretildi; geçmişi tutulmadı.',
    tone: 'pill-neutral',
  },
  not_applicable: {
    label: 'Uygulanmaz',
    hint: 'Kurum kaydı; kişisel LinkedIn profiline ekleme bu kayıt için geçerli değildir.',
    tone: 'pill-neutral',
  },
};

export const SHARE_BADGE: Record<ShareStatus, Badge> = {
  published: {
    label: 'Paylaşıldı',
    hint: "LinkedIn bu sunucuya bir gönderi kimliği döndürdü. Gönderinin LinkedIn'de hâlâ durduğu garanti edilmez.",
    tone: 'pill-ok',
  },
  result_unknown: {
    label: 'Sonuç bilinmiyor',
    hint: 'Yayımlama denendi, LinkedIn kesin bir yanıt vermedi. Gönderi oluşmuş olabilir.',
    tone: 'pill-warn',
  },
  started: {
    label: 'Paylaşım başlatıldı',
    hint: 'Bu kayıt için paylaşım başlatıldı. Gönderi oluşturulduğu bu kayda göre doğrulanmadı.',
    tone: 'pill-info',
  },
  none: {
    label: 'İşlem kaydı yok',
    hint: 'Takip başladığından beri bu kayıt için paylaşım başlatılmadı.',
    tone: 'pill-neutral',
  },
  unknown: {
    label: 'Bilinmiyor',
    hint: 'Bu kayıt, etkileşim takibi başlamadan önce üretildi; geçmişi tutulmadı.',
    tone: 'pill-neutral',
  },
};

/**
 * What a record whose engagement the surface never read should say.
 *
 * The demo has no engagement store and never will: its records are the prototype's own seed, and
 * inventing states for them would be inventing history. An organization record is «uygulanmaz» by
 * rule 13, and everything else is honestly «bilinmiyor».
 */
export function engagementFallback(isOrganization: boolean): EngagementView {
  return {
    profileAdd: isOrganization ? 'not_applicable' : 'unknown',
    share: 'unknown',
    publishedAt: null,
    postUrl: null,
  };
}
